import TypeHelper from '@/helpers/TypeHelper';
import ArrayHelper from '@/helpers/ArrayHelper';
import StringHelper from '@/helpers/StringHelper';
import EnumUtility from '@/enums/EnumUtility';
import { JobType } from '@/models/SearchResultModel';
import { DateColumn } from '@/components/utility/GridUtilities';
import { BasicPermissions } from '@/helpers/PermissionsHelper';
import { ScanType } from './wizard/ScanWizardClasses';

// This clones the same enum in services
export enum ScanCompletionStatus {
  None,
  Success,
  Canceled,
  NotStarted,
  NoUpdate,
  Removed,
  StopScan,
  QueueWaitTimeout,
  ScanCompletedObserved,
  AgentCompletedObserved,
  AgentReportedFailure
}

// This enum is mapped from an API enum so numbers are explicitely assigned for clarity
export enum SdmScanStatus {
  Ready = 0,
  Scanning = 1, // Scanning
  CompleteWithResults = 2,
  CompleteWithNoResults = 3,
  AbortPending = 4,
  Aborted = 5,
  Error = 6,
  ErrorRetrying = 7,
  ImportPending = 8,
  ImportProcessing = 9,
  ImportPostponed = 10,
  ResultUploading = 11
}

// Flags enum mapped from the API for Greyhound agent status
export enum ScanStatusFlags {
  Ready = 0,
  Scanning = 1,
  Completed = 2,
  Errors = 4,
  Results = 8,
  Failed = 16,
  Cancelled = 32,
  DiscoveryCompleted = 64 // maps to api but we do not show this on UI when display is rendered via the util below. It is only used on ScanStatusDialog.vue
}

export abstract class ScanStatusFlagsUtil {
  public static toDisplay(type: ScanStatusFlags): string {
    const getString = (t: ScanStatusFlags): string => {
      if (t === undefined) {
        t = 0;
      }

      switch (t) {
        case ScanStatusFlags.DiscoveryCompleted:
          // we hide this below but it needs to display properly if it is the only flag in type as noted below
          return 'Discovery Completed';
        default:
          return ScanStatusFlags[t];
      }
    };

    // hide this flag from display unless it is the only flag in the status
    if (type !== ScanStatusFlags.DiscoveryCompleted && EnumUtility.hasFlag(type, ScanStatusFlags.DiscoveryCompleted)) {
      type = EnumUtility.removeFlag(type, ScanStatusFlags.DiscoveryCompleted);
    }

    return EnumUtility.flagsEnumToDisplay(type, ScanStatusFlags, getString, null, false);
  }
}

// Clone of API flags enum
export enum ScanTypeFlags {
  None = 0,
  Discovery = 1,
  Search = 2,
  Remediation = 4,
  Sdm = 8,
  DataWatcher = 16,
  Distributed = 32,
  Agent = 64
}

export class ScanItem {
  public id: string;

  public name: string;

  public type: JobType;

  public agents: number;

  public endpointCount: number;

  public tagCount: number;

  public nextRun: Date;

  public lastRun: Date;

  public scanOwner: string;

  public isActive: boolean;

  public scanType: ScanTypeFlags;

  public status: SdmScanStatus | ScanStatusFlags = 0; // E.g. 0 = Ready/NotRun in both enumerations

  public editPermission: BasicPermissions = BasicPermissions.None;

  constructor(id: string, name: string, type: JobType, agents: number, endpointCount: number, tagCount: number, nextRun: Date, lastRun: Date, scanOwner: string, isActive: boolean, status: number, scanType: number, editPermission: BasicPermissions) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.agents = TypeHelper.isNull(agents) ? 0 : agents;
    this.endpointCount = TypeHelper.isNull(endpointCount) ? 0 : endpointCount;
    this.tagCount = TypeHelper.isNull(tagCount) ? 0 : tagCount;
    this.nextRun = nextRun;
    this.lastRun = lastRun;
    this.scanOwner = scanOwner;
    this.isActive = isActive;
    this.scanType = TypeHelper.isNull(scanType) ? ScanTypeFlags.Sdm /*Sdm is the default*/ : scanType;
    // The api should not return any rows without a minimum of the View permission so default to View just in case
    this.editPermission = editPermission || BasicPermissions.View;

    if (!TypeHelper.isNull(status)) {
      this.status = status;
    }
  }

  public get targetsExportDisplay(): string {
    return `${this.endpointCount} Target(s); ${this.tagCount} Tag(s)`;
  }

  public get targetsHtml(): string {
    return `<div>Targets: ${this.endpointCount}</div><div>Tags: ${this.tagCount}</div>`;
  }

  public get totalTargetsCount(): number { // used by scans grid for sorting
    return this.endpointCount + this.tagCount;
  }

  public get chipColor(): string {
    return this.isGreyhoundScan ? this.greyhoundChipColor : this.sdmChipColor;
  }

  private notRunDefaultColor = '#5FBBDA'; // blue

  private errorColor = '#b50d4d'; // red

  private completeWithResultsColor = '#5ba852'; // green

  private completeNoResultsColor = '#81B8B2'; // olive green

  private scanningColor = '#E8AA32'; // orange

  private greyColor = '#B7AFA9';

  private purpleColor = '#A975A0';

  public get sdmChipColor(): string {
    switch (this.status as SdmScanStatus) {
      case SdmScanStatus.Ready:
        return this.notRunDefaultColor;
      case SdmScanStatus.CompleteWithResults:
        return this.completeWithResultsColor;
      case SdmScanStatus.CompleteWithNoResults:
        return this.completeNoResultsColor;
      case SdmScanStatus.Scanning:
        return this.scanningColor;
      case SdmScanStatus.Error:
      case SdmScanStatus.ErrorRetrying:
        return this.errorColor;
      case SdmScanStatus.AbortPending:
      case SdmScanStatus.Aborted:
        return this.greyColor;
      case SdmScanStatus.ImportPending:
      case SdmScanStatus.ImportProcessing:
      case SdmScanStatus.ImportPostponed:
      case SdmScanStatus.ResultUploading:
        return this.purpleColor;
      default:
        return this.notRunDefaultColor;
    }
  }

  public get greyhoundChipColor(): string {
    if (!this.isGreyhoundScan) {
      // if not greyhound then the color won't be used in the UI but we should always return a valid color code just in case
      return this.purpleColor;
    }
    if (this.status === ScanStatusFlags.Ready) {
      return this.notRunDefaultColor;
    }
    // Completed states are a combination of scan status flags.  Error takes priority, then with results, scanning, failed/cancelled, default
    if (this.hasGreyhoundErrorStatus || this.hasGreyhoundFailedStatus) {
      return this.errorColor;
    }
    if (this.isGreyhoundCompletedWithResultsStatus) {
      return this.completeWithResultsColor;
    }
    if (this.isGreyhoundCompletedStatus) {
      return this.completeNoResultsColor;
    }
    if (this.isGreyhoundScanningStatus) {
      return this.scanningColor;
    }
    if (this.hasGreyhoundStatus(ScanStatusFlags.Failed) || this.hasGreyhoundStatus(ScanStatusFlags.Cancelled)) {
      return this.errorColor;
    }
    // The above should catch all cases but if not show the default grey
    return this.greyColor;
  }

  public get statusDisplay(): string {
    return this.isGreyhoundScan ? this.greyhoundStatusDisplay : this.sdmStatusDisplay;
  }

  private get sdmStatusDisplay(): string {
    switch (this.status) {
      case SdmScanStatus.Ready:
        return 'Not Run';
      case SdmScanStatus.Scanning:
        return 'Scanning';
      case SdmScanStatus.CompleteWithResults:
        return 'Done, With Results';
      case SdmScanStatus.CompleteWithNoResults:
        return 'Done, No Results';
      case SdmScanStatus.AbortPending:
        return 'Abort Processing';
      case SdmScanStatus.Aborted:
        return 'Aborted';
      case SdmScanStatus.Error:
        return 'Error';
      case SdmScanStatus.ErrorRetrying:
        return 'Error, Retry Pending';
      case SdmScanStatus.ImportPending:
        return 'Import Pending';
      case SdmScanStatus.ImportProcessing:
        return 'Import Processing';
      case SdmScanStatus.ImportPostponed:
        return 'Import Postponed';
      case SdmScanStatus.ResultUploading:
        return 'Results Uploading';
      default:
        return 'N/A'
    }
  }

  private get greyhoundStatusDisplay(): string {
    if (!this.isGreyhoundScan) {
      return '';
    }
    if (this.status === ScanStatusFlags.Ready) {
      return 'Not Run';
    }
    // Completed states are a combination of scan status flags.  Scanning with results takes priority, then scanning with errors, complete with results, complete no results
    if (this.isGreyhoundScanningWithResultsStatus) {
      return `Scanning, With Results${(this.hasGreyhoundErrorStatus ? ', Errors' : '')}`;
    }
    if (this.isGreyhoundScanningStatus) {
      return `Scanning${(this.hasGreyhoundErrorStatus ? ', With Errors' : '')}`;
    }
    // Failed status only shows when a scan is completed.
    if (this.isGreyhoundCompletedWithResultsStatus)
    {
      // A failure should not occur if the scan has results but just in case, to limit confusion, we show Errors for both the error and failed flags. E.g. Done, With Results, Failed would be confusing
      return `Done, With Results${(this.hasGreyhoundErrorStatus || this.hasGreyhoundFailedStatus ? ', Errors' : '')}`;
    }
    if (this.isGreyhoundCompletedStatus)
    {
      // It is not expected to have Error and Failed status but we show both just in case
      return `Done${(this.hasGreyhoundErrorStatus ? ', Errors' : '')}${(this.hasGreyhoundFailedStatus ? ', Failed' : '')}, No Results`;
    }
    // At this point we display each flag in this.status as comma separated
    return ScanStatusFlagsUtil.toDisplay(this.status as ScanStatusFlags);
  }

  private get hasGreyhoundErrorStatus(): boolean {
    return this.hasGreyhoundStatus(ScanStatusFlags.Errors);
  }

  private get hasGreyhoundFailedStatus(): boolean
  {
    return this.hasGreyhoundStatus(ScanStatusFlags.Failed);
  }

  private get isGreyhoundScanningStatus(): boolean {
    return this.isGreyhoundScan && this.hasGreyhoundStatus(ScanStatusFlags.Scanning);
  }

  private get isGreyhoundCompletedStatus(): boolean {
    return this.isGreyhoundScan && this.hasGreyhoundStatus(ScanStatusFlags.Completed);
  }

  private get isGreyhoundCompletedWithResultsStatus(): boolean {
    return this.isGreyhoundCompletedStatus && this.hasGreyhoundStatus(ScanStatusFlags.Results);
  }

  private get isGreyhoundCompletedWithErrorsStatus(): boolean {
    return this.isGreyhoundCompletedStatus && this.hasGreyhoundErrorStatus;
  }

  private get isGreyhoundScanningWithResultsStatus(): boolean {
    return this.isGreyhoundScan && this.hasGreyhoundStatus(ScanStatusFlags.Scanning) && this.hasGreyhoundStatus(ScanStatusFlags.Results);
  }

  public get isGreyhoundScan(): boolean {
    return TypeHelper.isNull(this.scanType) ? false : EnumUtility.hasFlag(this.scanType, ScanTypeFlags.Agent);
  }

  public get linkToResults(): boolean {
    if (this.isGreyhoundScan && this.hasGreyhoundStatus(ScanStatusFlags.Results)) {
      return true;
    }
    if (!this.isGreyhoundScan && this.status === SdmScanStatus.CompleteWithResults) {
      return true;
    }
    return false;
  }

  public get nextRunDisplay(): string {
    return !TypeHelper.isNull(this.nextRun) ? DateColumn.toDisplayString(this.nextRun, true) : 'N/A';
  }

  public get lastRunDisplay(): string {
    if (this.isScanning) {
      return 'In Progress';
    }
    return !TypeHelper.isNull(this.lastRun) ? DateColumn.toDisplayString(this.lastRun, true) : 'N/A';
  }

  public get typeDisplay(): string {
    if (this.isDiscoveryScan) {
      return 'Discovery';
    }
    if (this.isSensitiveDataScan) {
      return 'Sensitive Data';
    }
    return 'Unknown';
  }

  public get isScanning(): boolean { return (!this.isGreyhoundScan && this.status === SdmScanStatus.Scanning) || this.hasGreyhoundStatus(ScanStatusFlags.Scanning); }

  public get isDiscoveryScan(): boolean { return this.type === JobType.Discovery; }

  public get isSensitiveDataScan(): boolean { return this.type === JobType.Search; }

  private hasGreyhoundStatus(flag: ScanStatusFlags) {
    return this.isGreyhoundScan && EnumUtility.hasFlag(this.status, flag);
  }

  public get hasEditPermission(): boolean {
    return EnumUtility.hasFlag(this.editPermission, BasicPermissions.Edit);
  }

  public get hasViewPermission(): boolean {
    return EnumUtility.hasFlag(this.editPermission, BasicPermissions.View);
  }

  // NOTE: Data is not present from API for calculating this.  Leaving here for reference and possible future use.
  // eslint-disable-next-line
  public get lastRunDuration(): string {
    // NOTE: The code below was used on the old page and should work if start/end dates are supplied for calculating the duration.
    //let dateDiff = (endDateTime.getTime() - startDateTime.getTime()) / (1000)
    //const days = Math.floor(dateDiff / 86400)
    //dateDiff -= days * 86400
    //const hours = Math.floor(dateDiff / 3600) % 24
    //dateDiff -= hours * 3600
    //const minutes = Math.floor(dateDiff / 60) % 60
    //// dateDiff -= minutes * 60;
    //// var seconds = dateDiff % 60;
    //let duration = ''
    //if (days || days > 0) duration = `Days: ${days}`
    //if (hours || hours > 0) duration += ` Hours: ${hours}`
    //if (minutes || minutes > 0) duration += ` Minutes: ${minutes}`
    //return duration
    return 'N/A';
  }
}

export class QueueItem {
  public id: number;

  public name: string;

  public address: string;

  public port: string;

  public adminUser: string;

  public adminPassword: string;

  public restrictedUser: string;

  public restrictedUserPassword: string;

  constructor(id: number, name: string, address: string, port: number, adminUser: string, restrictedUser: string) {
    this.id = id;
    this.name = name;
    this.address = address;
    this.port = TypeHelper.isNull(port) ? null : port.toString(); // must be a string for UI controls
    this.adminUser = adminUser;
    this.restrictedUser = restrictedUser;
    // Passwords do not display in the grid and are not returned via the grid load API. These are used only when users create a new queue or manage and existing queue
    this.adminPassword = null;
    this.restrictedUserPassword = null;
  }

  public toApiPayload(isEdit: boolean) {
    return {
      id: isEdit ? this.id : undefined,
      name: StringHelper.trim(this.name),
      address: StringHelper.trim(this.address),
      // UI input for port is a text field so we use a string and then parse to a number for the API
      port: TypeHelper.isNull(this.port) || StringHelper.isNullOrWhiteSpace(this.port) ? null : parseInt(this.port, 10),
      adminUser: StringHelper.trim(this.adminUser),
      restrictedUser: StringHelper.trim(this.restrictedUser),
      // Do not trim passwords.  If a user adds spaces then we assume they meant to.  The API will keep the existing password if it is null ...
      // ... and overwrite when it is entered.  For new queues the password is required by the UI controls
      adminPassword: StringHelper.isNullOrWhiteSpace(this.adminPassword) ? null : this.adminPassword,
      restrictedUserPassword: StringHelper.isNullOrWhiteSpace(this.restrictedUserPassword) ? null : this.restrictedUserPassword,
    };
  }
}
